import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { GoogleChartInterface, GoogleChartType } from 'ng2-google-charts';
import { WebsiteAuditService } from '../website-audit.service';
import { DarkmodeService } from 'src/app/services/darkmode.service';

const ACCENT = '#6161ff';
const COMP_COLOR = '#e0a544';

// Distinct line colors for each tracked competitor in the trend chart (matches
// the Overview palette). COMP_COLOR (amber) stays first.
const COMP_PALETTE = [
  '#e0a544',
  '#5b9df0',
  '#c98ae0',
  '#5fcf95',
  '#e26a5d',
  '#4da18c',
  '#dd7bb0',
  '#c8e6a0',
];

const CAT_LABELS: { [k: string]: string } = {
  ux: 'UX',
  cr: 'Conversion Readiness',
  seo: 'SEO',
  geo: 'GEO',
  technical: 'Technical Foundations',
  diagnostic: 'Diagnostics',
};
const CAT_DESC: { [k: string]: string } = {
  ux: 'User experience across key areas',
  cr: 'Conversion drivers across the funnel',
  seo: 'Search visibility across key areas',
  geo: 'Generative-engine readiness',
  technical: 'Technical performance across key areas',
  diagnostic: 'Diagnostics across the site',
};

// Technical sub-categories don't carry a description in the API and the backend
// won't provide them, so fall back to these fixed descriptions.
const TECH_SUBCAT_DESC: { [k: string]: string } = {
  performance: 'Real-world loading, interactivity and visual stability.',
  javascript: 'JavaScript payload and main-thread cost.',
  layout: 'Layout stability and DOM efficiency.',
  network: 'Requests, caching and third parties.',
};

@Component({
  selector: 'app-audit-category',
  templateUrl: './audit-category.component.html',
  styleUrls: ['../audit-theme.css', './audit-category.component.css'],
})
export class AuditCategoryComponent implements OnInit, OnDestroy {
  @Input() sectionKey = '';

  constructor(
    private websiteAuditService: WebsiteAuditService,
    private darkmodeService: DarkmodeService,
  ) {}

  private subs = new Subscription();
  // Overlay shown while the sub-bar swaps competitors.
  public competitorSwitching$ =
    this.websiteAuditService.getCompetitorSwitching();

  accent = ACCENT;
  compColor = COMP_COLOR;

  // semantic colors — theme-aware
  green = '#5fcf95';
  red = '#e26a5d';
  amber = '#e0a544';
  gray = '#6b6660';

  private isDark = true;

  private applyThemeColors(isDark: boolean) {
    this.isDark = isDark;
    if (isDark) {
      this.green = '#5fcf95';
      this.red = '#e26a5d';
      this.amber = '#e0a544';
      this.gray = '#6b6660';
    } else {
      this.green = '#16a34a';
      this.red = '#d1483b';
      this.amber = '#b9791a';
      this.gray = '#6b7280';
    }
  }

  public isLoading = false;
  public hasData = false;

  public selectedTask: any;
  private section: any = null;

  // header
  public label = '';
  public description = '';
  public status = '';
  public statusColor = '';
  public statusBg = '';
  public score: number | null = null;
  public delta: number | null = null;
  public deltaColor = '';
  public deltaArrow = '';
  public deltaText = '';
  public compName = '';
  public hasComp = false;
  public comp: number | null = null;
  public optimal: number | null = null;
  public leadText = '';
  public leadColor = '';
  public rankText = '';

  // benchmark bars
  public youW = '0%';
  public compW = '0%';
  public optimalW = '0%';
  // One bar per tracked competitor (the selected one still drives ahead/behind).
  public benchCompetitors: {
    name: string;
    score: number;
    width: string;
    color: string;
  }[] = [];
  // All benchmark bars (your site + every competitor + Optimal where present)
  // ranked by score, highest first — each keeps its own label/color.
  public benchBars: {
    label: string;
    score: number;
    width: string;
    color: string;
    isYou?: boolean;
    isOptimal?: boolean;
  }[] = [];

  // trend (project-standard Google LineChart)
  public trendHasData = false;
  // Custom clickable dash legend (Google's built-in legend is hidden). Clicking
  // the competitor entry toggles its line; "Your site" stays locked on.
  public trendLegend: {
    key: string;
    label: string;
    color: string;
    isYou: boolean;
    visible: boolean;
  }[] = [];
  private trendVisible: { [key: string]: boolean } = {};
  public trendLineChart: GoogleChartInterface = {
    chartType: GoogleChartType.LineChart,
    dataTable: [],
    options: {
      legend: {
        position: 'top',
        alignment: 'center',
        textStyle: {
          fontSize: 12,
          fontName: 'Roboto, sans-serif',
          color: '#666',
        },
      },
      pointSize: 4,
      interpolateNulls: true,
      crosshair: {
        trigger: 'focus',
        orientation: 'vertical',
        color: '#eeeeee',
      },
      focusTarget: 'category',
      tooltip: { textStyle: { fontSize: 12 }, isHtml: true, trigger: 'focus' },
      colors: [ACCENT, COMP_COLOR],
      height: 240,
      backgroundColor: 'transparent',
      chartArea: {
        left: 40,
        right: 16,
        top: 30,
        bottom: 36,
        width: '100%',
        height: '70%',
      },
      vAxis: {
        minValue: 0,
        maxValue: 100,
        baseline: 0,
        baselineColor: '#c0c0c0',
        viewWindow: { min: 0, max: 100 },
        gridlineColor: '#ededed',
        textStyle: {
          fontSize: 11,
          fontName: 'Roboto, sans-serif',
          color: '#666',
        },
      },
      hAxis: {
        textStyle: {
          fontSize: 11,
          fontName: 'Roboto, sans-serif',
          color: '#666',
        },
        gridlines: { color: 'transparent' },
        minorGridlines: { count: 0 },
        baselineColor: 'transparent',
      },
    },
  };

  // sub-categories
  public subCards: any[] = [];
  public selectedSub: any = null;

  // page-performance metrics (Technical only)
  public techMetrics: any[] = [];

  ngOnInit(): void {
    this.subs.add(
      this.darkmodeService.getDarkMode().subscribe((isDark: boolean) => {
        // Follows the service value (the share shell sets it from the shared-with
        // theme), so the shared page matches the sharer's theme.
        this.applyThemeColors(!!isDark);
        if (this.selectedTask) this.build(this.selectedTask);
      }),
    );

    this.subs.add(
      this.websiteAuditService.getAuditTasksLoading().subscribe((v: any) => {
        this.isLoading = v;
      }),
    );

    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((task: any) => {
        this.selectedTask = task;
        this.build(task);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ─── Build ───────────────────────────────────────────────────────────────

  private build(task: any) {
    const key = this.sectionKey;
    const section = task?.sections?.[key] || null;
    this.section = section;

    const score = section?.score || task?.overview?.scores?.[key] || null;
    const hasScore =
      !!score && score.target !== null && score.target !== undefined;

    this.hasData = !!section && hasScore;
    if (!this.hasData) {
      this.subCards = [];
      this.selectedSub = null;
      this.techMetrics = [];
      return;
    }

    this.compName = this.getDomain(
      task?.selectedCompetitor?.domain || task?.selectedCompetitor?.url || '',
    );
    this.hasComp = score.competitor !== null && score.competitor !== undefined;

    this.label = CAT_LABELS[key] || section?.label || key.toUpperCase();
    this.description = CAT_DESC[key] || '';
    this.score = Math.round(score.target);
    this.comp = this.hasComp ? Math.round(score.competitor) : null;
    this.optimal =
      score.optimal !== null && score.optimal !== undefined
        ? Math.round(score.optimal)
        : null;

    this.status =
      this.capitalize(section?.status) || this.impactLabel(this.score);
    this.statusColor = this.impactColor(this.score);
    this.statusBg = this.rgba(this.statusColor, 0.13);

    this.delta = this.roundDelta(score.targetDelta);
    this.deltaColor =
      this.delta === null
        ? this.gray
        : this.delta > 0
          ? this.green
          : this.delta < 0
            ? this.red
            : this.gray;
    this.deltaArrow =
      this.delta === null
        ? ''
        : this.delta > 0
          ? '▲'
          : this.delta < 0
            ? '▼'
            : '→';
    this.deltaText =
      this.delta === null ? '' : `${this.delta > 0 ? '+' : ''}${this.delta}`;

    // benchmark
    this.youW = `${this.score}%`;
    this.compW = this.hasComp ? `${this.comp}%` : '0%';
    this.optimalW = this.optimal !== null ? `${this.optimal}%` : '0%';
    // Show a bar for EVERY tracked competitor, not just the selected one.
    this.benchCompetitors = this.buildBenchCompetitors(task, key);
    // Rank your site, every competitor, and Optimal (where present) together by
    // score, highest first.
    const bars: any[] = [
      {
        label: 'Your site',
        score: this.score,
        color: this.accent,
        isYou: true,
      },
      ...this.benchCompetitors.map((c) => ({
        label: c.name,
        score: c.score,
        color: c.color,
      })),
    ];
    if (this.optimal !== null) {
      bars.push({
        label: 'Optimal',
        score: this.optimal,
        color: this.gray,
        isOptimal: true,
      });
    }
    this.benchBars = bars
      .sort((a, b) => b.score - a.score)
      .map((b) => ({ ...b, width: `${b.score}%` }));

    if (this.hasComp) {
      const ahead = (this.score as number) >= (this.comp as number);
      this.leadColor = ahead ? this.green : this.red;
      this.leadText = ahead
        ? `Ahead of ${this.compName}`
        : `Behind ${this.compName}`;
      this.rankText = ahead ? '#1 of 2 tracked' : '#2 of 2 tracked';
    } else {
      this.leadColor = this.gray;
      this.leadText = 'No competitor tracked';
      this.rankText = '';
    }

    this.buildTrend(task, key);
    this.buildSubCards(section);
    this.buildMetrics(section);
  }

  // Every tracked competitor's current score for this category, taken from the
  // score history (the source that holds all competitors). Falls back to the
  // single selected competitor when there's no per-competitor history.
  private buildBenchCompetitors(task: any, key: string): any[] {
    const series = task?.overview?.scoreHistory?.series || [];
    const comps = series
      .filter((s: any) => s?.type === 'competitor')
      .map((s: any, idx: number) => {
        const val = this.seriesLatest(s?.scores?.[key]);
        return {
          name:
            this.getDomain(s?.domain || s?.url || s?.name || '') ||
            `Competitor ${idx + 1}`,
          score: val === null ? null : Math.round(val),
          color: COMP_PALETTE[idx % COMP_PALETTE.length],
        };
      })
      .filter((c: any) => c.score !== null)
      .map((c: any) => ({ ...c, width: `${c.score}%` }));

    if (comps.length) {
      return comps;
    }

    if (this.hasComp && this.comp !== null) {
      return [
        {
          name: this.compName || 'Competitor',
          score: this.comp,
          width: `${this.comp}%`,
          color: COMP_COLOR,
        },
      ];
    }
    return [];
  }

  // Latest non-null value in a score-history array.
  private seriesLatest(arr: any): number | null {
    const a = Array.isArray(arr) ? arr : [];
    for (let i = a.length - 1; i >= 0; i--) {
      const v = a[i];
      if (typeof v === 'number' && !isNaN(v)) return v;
    }
    return null;
  }

  // Page-performance metrics (present on the Technical section only).
  private buildMetrics(section: any) {
    const metrics = Array.isArray(section?.metrics) ? section.metrics : [];
    this.techMetrics = metrics
      .filter(
        (m: any) =>
          m?.score && m.score.target !== null && m.score.target !== undefined,
      )
      .map((m: any) => {
        const score = Math.round(m.score.target);
        const delta = this.roundDelta(m.score.targetDelta);
        const comp =
          m.score.competitor !== null && m.score.competitor !== undefined
            ? Math.round(m.score.competitor)
            : null;
        const lead = comp !== null ? score >= comp : true;
        return {
          name: m.name,
          value: m.targetValue,
          reason: m.reason || '',
          score,
          color: this.impactColor(score),
          delta,
          deltaColor:
            delta === null
              ? this.gray
              : delta > 0
                ? this.green
                : delta < 0
                  ? this.red
                  : this.gray,
          deltaArrow:
            delta === null ? '' : delta > 0 ? '▲' : delta < 0 ? '▼' : '→',
          deltaText: delta === null ? '' : `${delta > 0 ? '+' : ''}${delta}`,
          comp,
          hasComp: comp !== null,
          compColor: lead ? this.green : this.red,
          compArrow: lead ? '▲' : '▼',
        };
      });
  }

  // scoreHistory.series can carry one competitor entry per available competitor,
  // so match the one for the currently selected competitor (by id, then domain)
  // instead of always taking the first — otherwise switching competitors updates
  // the legend but leaves the plotted values pinned to the first competitor.
  private pickCompetitorSeries(series: any[], comp: any): any {
    const competitors = (series || []).filter(
      (s: any) => s?.type === 'competitor',
    );
    if (!competitors.length) return null;
    if (comp) {
      if (comp.competitorId) {
        const byId = competitors.find(
          (s: any) => s?.competitorId === comp.competitorId,
        );
        if (byId) return byId;
      }
      const compDomain = this.getDomain(comp.domain || comp.url || '');
      if (compDomain) {
        const byDomain = competitors.find(
          (s: any) =>
            this.getDomain(s?.domain || s?.url || s?.name || '') === compDomain,
        );
        if (byDomain) return byDomain;
      }
    }
    return competitors[0];
  }

  private buildTrend(task: any, key: string) {
    const hist = task?.overview?.scoreHistory;
    const dates: string[] = hist?.dates || [];
    const tSeries = (hist?.series || []).find((s: any) => s.type === 'target');

    // Every tracked competitor that has data for this section, each with a
    // stable color; visible ones are plotted, all appear in the legend.
    const comps = (hist?.series || [])
      .filter((s: any) => s?.type === 'competitor' && s?.scores?.[key])
      .map((s: any, idx: number) => ({
        series: s,
        key:
          s?.competitorId ||
          this.getDomain(s?.domain || s?.url || s?.name || '') ||
          `comp-${idx}`,
        label:
          this.getDomain(s?.domain || s?.url || s?.name || '') ||
          `Competitor ${idx + 1}`,
        color: COMP_PALETTE[idx % COMP_PALETTE.length],
      }));
    const visibleComps = comps.filter((c: any) => this.isTrendVisible(c.key));

    const header: any[] = [
      'Period',
      'Your site',
      { role: 'tooltip', p: { html: true } },
    ];
    visibleComps.forEach((c: any) => {
      header.push(c.label, { role: 'tooltip', p: { html: true } });
    });

    const rows = dates.map((d, i) => {
      const full = this.fmtDateFull(d);
      const you = this.numOrNull(tSeries?.scores?.[key]?.[i]);
      const compVals = visibleComps.map((c: any) =>
        this.numOrNull(c.series?.scores?.[key]?.[i]),
      );

      let tip =
        `<p class='tooltip-text'><b>${full}</b></p>` +
        `<p class='tooltip-text'><b>Your site: </b>${you !== null ? you : '—'}/100</p>`;
      visibleComps.forEach((c: any, idx: number) => {
        const cv = compVals[idx];
        if (cv !== null) {
          tip += `<p class='tooltip-text'><b>${c.label}: </b>${cv}/100</p>`;
        }
      });

      const row: any[] = [i, you, tip];
      visibleComps.forEach((c: any, idx: number) => {
        const cv = compVals[idx];
        row.push(cv, cv !== null ? tip : null);
      });
      return row;
    });

    const n = dates.length;
    this.trendLineChart.dataTable = rows.length ? [header, ...rows] : [];
    this.trendLineChart.options['colors'] = [
      ACCENT,
      ...visibleComps.map((c: any) => c.color),
    ];
    // Hide Google's built-in legend; render our own clickable dash legend.
    this.trendLineChart.options.legend.position = 'none';
    this.trendLegend = [
      {
        key: 'you',
        label: 'Your site',
        color: ACCENT,
        isYou: true,
        visible: true,
      },
      ...comps.map((c: any) => ({
        key: c.key,
        label: c.label,
        color: c.color,
        isYou: false,
        visible: this.isTrendVisible(c.key),
      })),
    ];
    this.trendLineChart.options.hAxis.ticks = dates.map((d, i) => ({
      v: i,
      f: this.fmtShort(d),
    }));
    this.trendLineChart.options.hAxis.viewWindow = {
      min: 0,
      max: n > 1 ? n - 1 : 1,
    };
    this.applyChartTheme();
    // Only a biweekly audit with more than one data point has a meaningful
    // trend — hide the chart for one-time audits and single-point histories.
    this.trendHasData = rows.length > 1 && this.isBiweekly;
    setTimeout(
      () => this.trendLineChart.component?.draw(this.trendLineChart),
      0,
    );
  }

  private isTrendVisible(key: string): boolean {
    return this.trendVisible[key] !== false;
  }
  // Legend click — toggle a competitor line ("Your site" is always shown).
  toggleTrendLine(item: any) {
    if (!item || item.isYou) return;
    this.trendVisible[item.key] = !this.isTrendVisible(item.key);
    this.buildTrend(this.selectedTask, this.sectionKey);
  }

  private numOrNull(v: any): number | null {
    // Scores over time are shown as whole numbers (e.g. 55.32 → 55).
    return typeof v === 'number' && !isNaN(v) ? Math.round(v) : null;
  }

  private applyChartTheme() {
    const o: any = this.trendLineChart.options;
    o.backgroundColor = 'transparent';
    if (this.isDark) {
      o.legend.textStyle.color = '#cfccc6';
      o.vAxis.gridlineColor = '#3b3a3a';
      o.vAxis.baselineColor = '#3b3a3a';
      o.vAxis.textStyle.color = '#cfccc6';
      o.hAxis.textStyle.color = '#cfccc6';
      o.crosshair.color = '#3b3a3a';
    } else {
      o.legend.textStyle.color = '#666';
      o.vAxis.gridlineColor = '#ededed';
      o.vAxis.baselineColor = '#c0c0c0';
      o.vAxis.textStyle.color = '#666';
      o.hAxis.textStyle.color = '#666';
      o.crosshair.color = '#eeeeee';
    }
  }

  private fmtDateFull(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return date;
    }
  }

  private buildSubCards(section: any) {
    const cats = section?.categories || [];
    this.subCards = cats.map((c: any) => {
      const s = c?.score || {};
      const cur =
        s.target !== null && s.target !== undefined
          ? Math.round(s.target)
          : null;
      const comp =
        s.competitor !== null && s.competitor !== undefined
          ? Math.round(s.competitor)
          : null;
      const delta = this.roundDelta(s.targetDelta);
      const lead = cur !== null && comp !== null ? cur >= comp : true;
      return {
        raw: c,
        name: c?.name || c?.label || c?.key || '',
        description: this.subDesc(c),
        cur,
        comp,
        hasComp: comp !== null,
        delta,
        deltaColor:
          delta === null
            ? this.gray
            : delta > 0
              ? this.green
              : delta < 0
                ? this.red
                : this.gray,
        deltaArrow:
          delta === null ? '' : delta > 0 ? '▲' : delta < 0 ? '▼' : '→',
        deltaText: delta === null ? '' : `${delta > 0 ? '+' : ''}${delta}`,
        compColor: lead ? this.green : this.red,
        compArrow: lead ? '▲' : '▼',
        selected: false,
      };
    });

    this.selectSub(this.subCards[0]);
  }

  selectSub(card: any) {
    if (!card) {
      this.selectedSub = null;
      return;
    }
    this.subCards.forEach((c) => (c.selected = false));
    card.selected = true;

    const c = card.raw;
    const s = c?.score || {};
    const lead =
      card.cur !== null && card.comp !== null ? card.cur >= card.comp : true;

    const signals = this.buildSignals(c);

    this.selectedSub = {
      name: c?.name || c?.label || c?.key || '',
      description: this.subDesc(c),
      score: card.cur,
      delta: card.delta,
      deltaColor: card.deltaColor,
      deltaArrow: card.deltaArrow,
      deltaText: card.deltaText,
      comp: card.comp,
      hasComp: card.hasComp,
      compColor: lead ? this.green : this.red,
      compArrow: lead ? '▲' : '▼',
      compName: this.compName,
      target:
        s.optimal !== null && s.optimal !== undefined
          ? Math.round(s.optimal)
          : null,
      ...signals,
    };
  }

  // Two section shapes:
  //  • UX/CR   → target/competitor.workingWell + notWorkingWell ({ text, reason })
  //  • Technical → status-tagged issues[] (pass/warning/fail/info), now split per
  //    side under target.issues / competitor.issues (older payloads keep a flat
  //    top-level issues[] with no competitor breakdown).
  private buildSignals(c: any): {
    issues: any[];
    positives: any[];
    compIssues: any[];
    compPositives: any[];
  } {
    // Detect the UX/CR shape by its working-well fields specifically — the
    // Technical shape now also carries a `competitor` object, so the presence
    // of `c.competitor` alone can no longer distinguish the two.
    const isWorkingWellShape =
      c?.target?.workingWell ||
      c?.target?.notWorkingWell ||
      c?.competitor?.workingWell ||
      c?.competitor?.notWorkingWell;

    if (isWorkingWellShape) {
      return {
        issues: this.mapSignals(c?.target?.notWorkingWell),
        positives: this.mapSignals(c?.target?.workingWell),
        compIssues: this.mapSignals(c?.competitor?.notWorkingWell),
        compPositives: this.mapSignals(c?.competitor?.workingWell),
      };
    }

    // Technical: your-site issues under target.issues, competitor under
    // competitor.issues (fall back to the legacy flat list for the target).
    const targetList = Array.isArray(c?.target?.issues)
      ? c.target.issues
      : Array.isArray(c?.issues)
        ? c.issues
        : [];
    const compList = Array.isArray(c?.competitor?.issues)
      ? c.competitor.issues
      : [];

    const target = this.splitStatusIssues(targetList);
    const comp = this.splitStatusIssues(compList);

    return {
      issues: target.issues,
      positives: target.positives,
      compIssues: comp.issues,
      compPositives: comp.positives,
    };
  }

  // Split a status-tagged issue list into failing/warning issues and passing
  // positives, mapped to the { title, desc, score } shape the template renders.
  private splitStatusIssues(list: any[]): { issues: any[]; positives: any[] } {
    const arr = Array.isArray(list) ? list : [];
    const map = (i: any) => ({
      title: i?.title,
      desc: i?.reason,
      score: i?.score,
    });
    const issues = arr
      .filter((i: any) => i?.status === 'fail' || i?.status === 'warning')
      .sort((a: any, b: any) => (a?.score ?? 0) - (b?.score ?? 0))
      .slice(0, 8)
      .map(map);
    const positives = arr
      .filter((i: any) => i?.status === 'pass')
      .sort((a: any, b: any) => (b?.score ?? 0) - (a?.score ?? 0))
      .slice(0, 5)
      .map(map);
    return { issues, positives };
  }

  private mapSignals(arr: any[]): any[] {
    return (arr || []).map((o) => ({
      title: o?.text,
      desc: o?.reason,
      score: o?.score,
    }));
  }

  private subDesc(c: any): string {
    return (
      c?.description ||
      TECH_SUBCAT_DESC[(c?.key || c?.label || '').toLowerCase()] ||
      ''
    );
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  // One-time audits have no history, so the score-over-time trend is hidden.
  get isBiweekly(): boolean {
    return (this.selectedTask?.auditFrequency || '') === 'biweekly';
  }

  // UX and CR hide the status badge entirely; the others (e.g. Technical) show
  // it, but never for a "Warning" — only Critical / Good etc.
  get showStatusPill(): boolean {
    if (this.sectionKey === 'ux' || this.sectionKey === 'cr') return false;
    return (this.status || '').toLowerCase() !== 'warning';
  }

  impactLabel(num: number): string {
    if (typeof num !== 'number') return 'No data';
    if (num < 50) return 'Critical';
    if (num < 80) return 'Warning';
    return 'Good';
  }
  impactColor(num: number): string {
    if (typeof num !== 'number') return this.gray;
    if (num < 50) return this.red;
    if (num < 80) return this.amber;
    return this.green;
  }

  private roundDelta(d: any): number | null {
    if (d === null || d === undefined) return null;
    return Math.round(d * 10) / 10;
  }
  private capitalize(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  rgba(hex: string, a: number): string {
    const h = (hex || '').replace('#', '');
    if (h.length < 6) return `rgba(107,102,96,${a})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  }

  getDomain(url: string): string {
    if (!url) return '';
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      return new URL(normalized).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  private fmtShort(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return date;
    }
  }
}
